const STUDIO_GOOGLE_ADS_LOADER_SELECTOR = 'script[data-cove-studio-gads-loader]';
const STUDIO_GOOGLE_ADS_BOOTSTRAP_SELECTOR = 'script[data-cove-studio-gads-bootstrap]';
const STUDIO_GOOGLE_ADS_CONFIG_SELECTOR = 'script[data-cove-studio-gads-config]';
const STUDIO_GOOGLE_ADS_RUNTIME_SELECTOR = 'script[data-cove-studio-gads-runtime]';
export const STUDIO_PREVIEW_WINDOW_NAME = 'cove-studio-preview';
const EXISTING_GOOGLE_TAG_LOADER_SELECTOR =
  'script[src*="googletagmanager.com/gtag/js"], script[src*="www.googletagmanager.com/gtag/js"]';
const TAG_ID_RE = /^AW-\d+$/;
const SUPPORTED_SIGNAL_TYPES = new Set(['form', 'cta', 'time', 'scroll']);
const SUPPORTED_SIGNAL_BINDING_TYPES = new Set([
  'gt_event',
  'gt_entity',
  'element_id',
  'css_selector',
]);

export type StudioGoogleAdsSignalType = 'form' | 'cta' | 'time' | 'scroll';
export type StudioGoogleAdsSignalBindingType =
  | 'gt_event'
  | 'gt_entity'
  | 'element_id'
  | 'css_selector';

export interface StudioGoogleAdsSignal {
  type: StudioGoogleAdsSignalType;
  label: string;
  sourceBindingType?: StudioGoogleAdsSignalBindingType;
  sourceEventKey?: string;
  sourceEventLabel?: string;
}

export interface StudioGoogleAdsSettings {
  tagId: string;
  signals: StudioGoogleAdsSignal[];
  timeThresholdMs?: number;
  scrollThresholdPercent?: number;
}

const trimValue = (value: unknown) => String(value || '').trim();

const serializeInlineJson = (value: unknown) =>
  JSON.stringify(value)
    .replace(/</g, '\\u003C')
    .replace(/-->/g, '--\\u003E')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

const normalizeThreshold = (value: unknown, fallback: number) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
};

const normalizeSignalBindingType = (value: unknown) => {
  const bindingType = trimValue(value);
  if (!SUPPORTED_SIGNAL_BINDING_TYPES.has(bindingType)) return undefined;
  return bindingType as StudioGoogleAdsSignalBindingType;
};

export const normalizeStudioGoogleAdsSettings = (
  settings?: Partial<StudioGoogleAdsSettings> | null,
): StudioGoogleAdsSettings => {
  const rawSignals = Array.isArray(settings?.signals) ? settings.signals : [];
  const normalizedSignals: StudioGoogleAdsSignal[] = [];

  rawSignals.forEach((signal) => {
    const type = trimValue(signal?.type) as StudioGoogleAdsSignalType;
    const label = trimValue(signal?.label);
    if (!SUPPORTED_SIGNAL_TYPES.has(type) || !label) return;

    const sourceBindingType = normalizeSignalBindingType(signal?.sourceBindingType);
    const sourceEventKey = trimValue(signal?.sourceEventKey);
    const sourceEventLabel = trimValue(signal?.sourceEventLabel);

    normalizedSignals.push({
      type,
      label,
      sourceBindingType:
        (type === 'form' || type === 'cta') && sourceBindingType && sourceEventKey
          ? sourceBindingType
          : undefined,
      sourceEventKey:
        (type === 'form' || type === 'cta') && sourceBindingType && sourceEventKey
          ? sourceEventKey
          : undefined,
      sourceEventLabel:
        (type === 'form' || type === 'cta') && sourceBindingType && sourceEventKey
          ? sourceEventLabel || undefined
          : undefined,
    });
  });

  return {
    tagId: trimValue(settings?.tagId),
    signals: normalizedSignals,
    timeThresholdMs: normalizeThreshold(settings?.timeThresholdMs, 120_000),
    scrollThresholdPercent: normalizeThreshold(settings?.scrollThresholdPercent, 75),
  };
};

export const parseStudioGoogleAdsSettingsFromHtml = (htmlContent: string) => {
  if (!trimValue(htmlContent)) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const configNode = doc.querySelector(STUDIO_GOOGLE_ADS_CONFIG_SELECTOR);
  const loaderNode = doc.querySelector<HTMLScriptElement>(STUDIO_GOOGLE_ADS_LOADER_SELECTOR);

  const fallbackTagId =
    trimValue(loaderNode?.getAttribute('src')).match(/(?:\?|&)id=(AW-\d+)/i)?.[1] || '';

  if (!configNode?.textContent) {
    if (!fallbackTagId) return null;
    return normalizeStudioGoogleAdsSettings({ tagId: fallbackTagId, signals: [] });
  }

  try {
    const parsed = JSON.parse(configNode.textContent) as Partial<StudioGoogleAdsSettings>;
    return normalizeStudioGoogleAdsSettings({
      ...parsed,
      tagId: trimValue(parsed?.tagId) || fallbackTagId,
    });
  } catch {
    if (!fallbackTagId) return null;
    return normalizeStudioGoogleAdsSettings({ tagId: fallbackTagId, signals: [] });
  }
};

const removeStudioGoogleAdsScripts = (doc: Document) => {
  doc.querySelectorAll(STUDIO_GOOGLE_ADS_LOADER_SELECTOR).forEach((node) => node.remove());
  doc.querySelectorAll(STUDIO_GOOGLE_ADS_BOOTSTRAP_SELECTOR).forEach((node) => node.remove());
  doc.querySelectorAll(STUDIO_GOOGLE_ADS_CONFIG_SELECTOR).forEach((node) => node.remove());
  doc.querySelectorAll(STUDIO_GOOGLE_ADS_RUNTIME_SELECTOR).forEach((node) => node.remove());
};

export const removeStudioGoogleAdsTrackingScriptsFromDocument = (
  doc: Document | null | undefined,
) => {
  if (!doc) return;
  removeStudioGoogleAdsScripts(doc);
};

export const stripStudioGoogleAdsTrackingScript = (htmlContent: string) => {
  if (!trimValue(htmlContent)) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  removeStudioGoogleAdsScripts(doc);
  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};

const ensureDocumentHead = (doc: Document) => {
  const htmlElement = doc.documentElement;
  if (!htmlElement) return null;

  let head = doc.head;
  if (!head) {
    head = doc.createElement('head');
    if (doc.body) {
      htmlElement.insertBefore(head, doc.body);
    } else {
      htmlElement.appendChild(head);
    }
  }

  return head;
};

const buildGoogleAdsBootstrapScript = (tagId: string) => {
  const serializedTagId = serializeInlineJson(tagId);
  const serializedPreviewWindowName = serializeInlineJson(STUDIO_PREVIEW_WINDOW_NAME);

  return `(function () {
  try {
    if (window.name === ${serializedPreviewWindowName}) return;

    var tagId = ${serializedTagId};
    if (!/^AW-\\d+$/.test(tagId)) return;

    var hadGtag = typeof window.gtag === 'function';
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments);
    };

    window.__coveStudioGoogleAdsConfiguredTags =
      window.__coveStudioGoogleAdsConfiguredTags || {};

    if (!hadGtag) {
      window.gtag('js', new Date());
    }

    if (window.__coveStudioGoogleAdsConfiguredTags[tagId]) return;
    window.gtag('config', tagId);
    window.__coveStudioGoogleAdsConfiguredTags[tagId] = true;
  } catch (error) {
    console.warn('[StudioGoogleAds] Failed to bootstrap Google tag.', error);
  }
})();`;
};

const buildGoogleAdsRuntimeScript = () =>
  `(function () {
  try {
    if (window.name === ${serializeInlineJson(STUDIO_PREVIEW_WINDOW_NAME)}) {
      return;
    }

    if (
      document.querySelector('script[data-linker-init]') ||
      document.querySelector('script[src*="linker-child.umd.aiu4qs6n.js"]')
    ) {
      return;
    }

    var configNode = document.querySelector(${serializeInlineJson(STUDIO_GOOGLE_ADS_CONFIG_SELECTOR)});
    if (!configNode) return;

    var rawConfig = {};
    try {
      rawConfig = JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      console.warn('[StudioGoogleAds] Failed to parse config.', error);
      return;
    }

    var tagId = typeof rawConfig.tagId === 'string' ? rawConfig.tagId.trim() : '';
    if (!/^AW-\\d+$/.test(tagId)) return;

    var signals = Array.isArray(rawConfig.signals) ? rawConfig.signals : [];
    var timeThresholdMs =
      typeof rawConfig.timeThresholdMs === 'number' && rawConfig.timeThresholdMs > 0
        ? rawConfig.timeThresholdMs
        : 120000;
    var scrollThresholdPercent =
      typeof rawConfig.scrollThresholdPercent === 'number' &&
      rawConfig.scrollThresholdPercent > 0
        ? rawConfig.scrollThresholdPercent
        : 75;

    var isSupportedSignalType = function (type) {
      return type === 'form' || type === 'cta' || type === 'time' || type === 'scroll';
    };

    var activeSignalsByType = {
      form: [],
      cta: [],
      time: [],
      scroll: [],
    };
    for (var index = 0; index < signals.length; index += 1) {
      var signal = signals[index] || {};
      var type = typeof signal.type === 'string' ? signal.type.trim() : '';
      var label = typeof signal.label === 'string' ? signal.label.trim() : '';
      if (
        (type === 'form' || type === 'cta' || type === 'time' || type === 'scroll') &&
        label
      ) {
        activeSignalsByType[type].push({
          label: label,
          bindingType:
            typeof signal.sourceBindingType === 'string' ? signal.sourceBindingType.trim() : '',
          eventKey: typeof signal.sourceEventKey === 'string' ? signal.sourceEventKey.trim() : '',
          eventLabel:
            typeof signal.sourceEventLabel === 'string' ? signal.sourceEventLabel.trim() : '',
        });
      }
    }

    if (
      !activeSignalsByType.form.length &&
      !activeSignalsByType.cta.length &&
      !activeSignalsByType.time.length &&
      !activeSignalsByType.scroll.length
    ) {
      return;
    }

    window.__coveStudioGoogleAdsTrackingState =
      window.__coveStudioGoogleAdsTrackingState || {
        fired: {},
        initialized: {},
      };

    var fired = window.__coveStudioGoogleAdsTrackingState.fired;
    var initialized = window.__coveStudioGoogleAdsTrackingState.initialized;

    var buildSignalStateKey = function (type, signalConfigs) {
      return tagId + ':' + type + ':' + signalConfigs
        .map(function (signalConfig) {
          return [
            signalConfig.label || '',
            signalConfig.bindingType || '',
            signalConfig.eventKey || '',
            signalConfig.eventLabel || '',
          ].join('|');
        })
        .join('||');
    };

    var fireConversion = function (type, signalConfig, extraParams) {
      var label = signalConfig && signalConfig.label;
      if (!label) return;

      var fireKey = tagId + ':' + type + ':' + label;
      if (fired[fireKey]) return;
      if (typeof window.gtag !== 'function') return;

      fired[fireKey] = true;
      var payload = {
        send_to: tagId + '/' + label,
      };

      if (extraParams && typeof extraParams === 'object') {
        for (var paramKey in extraParams) {
          if (Object.prototype.hasOwnProperty.call(extraParams, paramKey)) {
            payload[paramKey] = extraParams[paramKey];
          }
        }
      }

      window.gtag('event', 'conversion', payload);
    };

    var matchesBoundElement = function (boundElement, candidate) {
      if (!boundElement || !candidate) return false;

      return (
        boundElement === candidate ||
        (typeof boundElement.contains === 'function' && boundElement.contains(candidate)) ||
        (typeof candidate.contains === 'function' && candidate.contains(boundElement))
      );
    };

    var escapeAttributeValue = function (value) {
      return String(value || '')
        .replace(/\\\\/g, '\\\\\\\\')
        .replace(/"/g, '\\"');
    };

    var normalizeEventElement = function (value) {
      if (!value) return null;
      if (value.nodeType === 1) return value;
      return value.parentElement || null;
    };

    var matchesClosestSelector = function (element, selector) {
      if (!element || !selector || typeof element.closest !== 'function') return false;
      try {
        return !!element.closest(selector);
      } catch (error) {
        console.warn('[StudioGoogleAds] Invalid selector binding.', error);
        return false;
      }
    };

    var matchesSignalBinding = function (signalConfig, candidate, originalTarget) {
      if (!signalConfig || !candidate) return false;

      var bindingType = signalConfig.bindingType;
      var eventKey = signalConfig.eventKey;
      if (!bindingType || !eventKey) return false;

      var normalizedOriginalTarget = normalizeEventElement(originalTarget);

      switch (bindingType) {
        case 'element_id': {
          var boundElement = document.getElementById(eventKey);
          return (
            matchesBoundElement(boundElement, candidate) ||
            matchesBoundElement(boundElement, normalizedOriginalTarget)
          );
        }
        case 'css_selector':
          return (
            matchesClosestSelector(candidate, eventKey) ||
            matchesClosestSelector(normalizedOriginalTarget, eventKey)
          );
        case 'gt_event':
          return (
            matchesClosestSelector(
              candidate,
              '[data-gt-event="' + escapeAttributeValue(eventKey) + '"]',
            ) ||
            matchesClosestSelector(
              normalizedOriginalTarget,
              '[data-gt-event="' + escapeAttributeValue(eventKey) + '"]',
            )
          );
        case 'gt_entity':
          return (
            matchesClosestSelector(
              candidate,
              '[data-gt-entity="' + escapeAttributeValue(eventKey) + '"]',
            ) ||
            matchesClosestSelector(
              normalizedOriginalTarget,
              '[data-gt-entity="' + escapeAttributeValue(eventKey) + '"]',
            )
          );
        default:
          return false;
      }
    };

    var isInIgnoredContainer = function (element) {
      return Boolean(element && element.closest && element.closest('nav, footer'));
    };

    var isButtonLikeAnchor = function (element) {
      if (!element || element.tagName !== 'A') return false;

      var className = typeof element.className === 'string' ? element.className : '';
      var href = typeof element.getAttribute === 'function' ? element.getAttribute('href') || '' : '';
      return (
        element.getAttribute('role') === 'button' ||
        /(^|\\s)(btn|button|cta)(-|\\s|$)/i.test(className) ||
        element.hasAttribute('data-cove-cta') ||
        element.hasAttribute('data-cove-gads-cta') ||
        element.hasAttribute('data-gt-event') ||
        element.hasAttribute('data-gt-entity') ||
        href.indexOf('#') === 0 ||
        /^mailto:|^tel:/i.test(href)
      );
    };

    var isTrackableCta = function (element) {
      if (!element || isInIgnoredContainer(element)) return false;

      if (element.tagName === 'BUTTON') return true;
      if (element.tagName === 'INPUT') {
        var inputType = typeof element.type === 'string' ? element.type.toLowerCase() : '';
        return inputType === 'button' || inputType === 'submit';
      }
      if (element.getAttribute && element.getAttribute('role') === 'button') return true;
      if (isButtonLikeAnchor(element)) return true;
      return false;
    };

    if (activeSignalsByType.form.length) {
      var formSignals = activeSignalsByType.form;
      var formInitKey = buildSignalStateKey('form', formSignals);
      if (!initialized[formInitKey]) {
        initialized[formInitKey] = true;

        var handleFormSubmit = function (event) {
          var form = event && event.target;
          if (!form || typeof form.tagName !== 'string' || form.tagName !== 'FORM') return;
          var submitter = event && event.submitter;
          for (var formIndex = 0; formIndex < formSignals.length; formIndex += 1) {
            var formSignal = formSignals[formIndex];
            if (
              formSignal.bindingType &&
              formSignal.eventKey &&
              !matchesSignalBinding(formSignal, form, submitter || form)
            ) {
              continue;
            }
            fireConversion('form', formSignal);
          }
        };

        document.addEventListener('submit', handleFormSubmit, true);
      }
    }

    if (activeSignalsByType.cta.length) {
      var ctaSignals = activeSignalsByType.cta;
      var ctaInitKey = buildSignalStateKey('cta', ctaSignals);
      if (!initialized[ctaInitKey]) {
        initialized[ctaInitKey] = true;

        var handleCtaClick = function (event) {
          var target = event && event.target;
          if (!target || !target.closest) return;

          var candidate = target.closest(
            'button, a[href], [role="button"], input[type="button"], input[type="submit"]',
          );
          if (!candidate || !isTrackableCta(candidate)) return;
          for (var ctaIndex = 0; ctaIndex < ctaSignals.length; ctaIndex += 1) {
            var ctaSignal = ctaSignals[ctaIndex];
            if (
              ctaSignal.bindingType &&
              ctaSignal.eventKey &&
              !matchesSignalBinding(ctaSignal, candidate, target)
            ) {
              continue;
            }
            fireConversion('cta', ctaSignal);
          }
        };

        document.addEventListener('click', handleCtaClick, true);
      }
    }

    if (activeSignalsByType.time.length) {
      var timeSignals = activeSignalsByType.time;
      var timeInitKey = buildSignalStateKey('time', timeSignals);
      if (!initialized[timeInitKey]) {
        initialized[timeInitKey] = true;

        var remainingVisibleMs = timeThresholdMs;
        var visibleSince = document.visibilityState === 'hidden' ? 0 : Date.now();
        var timeTimerId = 0;

        var clearTimeTimer = function () {
          if (!timeTimerId) return;
          window.clearTimeout(timeTimerId);
          timeTimerId = 0;
        };

        var finalizeVisibleTime = function () {
          if (!visibleSince) return;
          remainingVisibleMs = Math.max(0, remainingVisibleMs - (Date.now() - visibleSince));
          visibleSince = 0;
        };

        var cleanupTimeTracking = function () {
          clearTimeTimer();
          document.removeEventListener('visibilitychange', handleTimeVisibilityChange);
          window.removeEventListener('pagehide', cleanupTimeTracking);
        };

        var scheduleTimeTracking = function () {
          clearTimeTimer();
          if (fired[timeInitKey]) {
            cleanupTimeTracking();
            return;
          }
          if (document.visibilityState === 'hidden') {
            visibleSince = 0;
            return;
          }

          visibleSince = Date.now();
          timeTimerId = window.setTimeout(function () {
            finalizeVisibleTime();
            cleanupTimeTracking();
            for (var timeIndex = 0; timeIndex < timeSignals.length; timeIndex += 1) {
              fireConversion('time', timeSignals[timeIndex]);
            }
          }, remainingVisibleMs);
        };

        var handleTimeVisibilityChange = function () {
          if (document.visibilityState === 'hidden') {
            finalizeVisibleTime();
            clearTimeTimer();
            return;
          }
          scheduleTimeTracking();
        };

        scheduleTimeTracking();
        document.addEventListener('visibilitychange', handleTimeVisibilityChange);
        window.addEventListener('pagehide', cleanupTimeTracking);
      }
    }

    if (activeSignalsByType.scroll.length) {
      var scrollSignals = activeSignalsByType.scroll;
      var scrollInitKey = buildSignalStateKey('scroll', scrollSignals);
      if (!initialized[scrollInitKey]) {
        initialized[scrollInitKey] = true;

        var cleanupScrollTracking = function () {
          window.removeEventListener('scroll', handleScrollTracking);
          window.removeEventListener('resize', handleScrollTracking);
        };

        var handleScrollTracking = function () {
          if (fired[scrollInitKey]) {
            cleanupScrollTracking();
            return;
          }

          var docEl = document.documentElement;
          var body = document.body;
          var scrollTop =
            window.pageYOffset ||
            (docEl && docEl.scrollTop) ||
            (body && body.scrollTop) ||
            0;
          var viewportHeight =
            window.innerHeight ||
            (docEl && docEl.clientHeight) ||
            (body && body.clientHeight) ||
            0;
          var scrollHeight = Math.max(
            (docEl && docEl.scrollHeight) || 0,
            (body && body.scrollHeight) || 0,
            viewportHeight,
          );
          var currentDepthPercent = ((scrollTop + viewportHeight) / scrollHeight) * 100;

          if (currentDepthPercent >= scrollThresholdPercent) {
            cleanupScrollTracking();
            for (var scrollIndex = 0; scrollIndex < scrollSignals.length; scrollIndex += 1) {
              fireConversion('scroll', scrollSignals[scrollIndex], {
                scroll_threshold: scrollThresholdPercent,
              });
            }
          }
        };

        window.addEventListener('scroll', handleScrollTracking, { passive: true });
        window.addEventListener('resize', handleScrollTracking, { passive: true });
        handleScrollTracking();
      }
    }
  } catch (error) {
    console.warn('[StudioGoogleAds] Failed to initialize conversion runtime.', error);
  }
})();`;

export const upsertStudioGoogleAdsInHtml = (
  htmlContent: string,
  settings: StudioGoogleAdsSettings,
) => {
  if (!trimValue(htmlContent)) return htmlContent;

  const normalizedSettings = normalizeStudioGoogleAdsSettings(settings);
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const head = ensureDocumentHead(doc);
  if (!head) return htmlContent;

  removeStudioGoogleAdsScripts(doc);

  if (!TAG_ID_RE.test(normalizedSettings.tagId)) {
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  }

  if (!doc.querySelector(EXISTING_GOOGLE_TAG_LOADER_SELECTOR)) {
    const loaderNode = doc.createElement('script');
    loaderNode.setAttribute('async', 'true');
    loaderNode.setAttribute(
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${normalizedSettings.tagId}`,
    );
    loaderNode.setAttribute('data-cove-studio-gads-loader', 'true');
    head.appendChild(loaderNode);
  }

  const bootstrapNode = doc.createElement('script');
  bootstrapNode.setAttribute('data-cove-studio-gads-bootstrap', 'true');
  bootstrapNode.textContent = buildGoogleAdsBootstrapScript(normalizedSettings.tagId);
  head.appendChild(bootstrapNode);

  if (normalizedSettings.signals.length > 0) {
    const configNode = doc.createElement('script');
    configNode.setAttribute('type', 'application/json');
    configNode.setAttribute('data-cove-studio-gads-config', 'true');
    configNode.textContent = serializeInlineJson(normalizedSettings);
    head.appendChild(configNode);

    const runtimeNode = doc.createElement('script');
    runtimeNode.setAttribute('data-cove-studio-gads-runtime', 'true');
    runtimeNode.textContent = buildGoogleAdsRuntimeScript();
    head.appendChild(runtimeNode);
  }

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};
